import { useEffect, useState } from 'react';
import {
  ButtonSize,
  IconLogout,
  ButtonSecondary,
  ToggleInput,
  IconInfo,
  NotifiactionHintPopup,
  NotifiactionDisabledPopup,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setBrowserNotificationEnabled } from '../../hoc/settings/profile/store/slices';
import FiltersPresets from './filters-presets';
import { ProfileGeneralTypes } from './types';

import cn from './profile.module.css';

const phoneFormat = (phone: string) =>
  phone.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');

const ProfileGeneral = ({
  employeeProfile,
  roles,
  logout,
  havePermissions,
  logoutInProgress,
}: ProfileGeneralTypes) => {
  const {
    settings: {
      profile: { browserNotificationEnabled },
    },
    root: { userId },
  } = useAppSelector((state) => state);

  const [notificationInBrowserEnabled, setNotificationInBrowserEnabled] =
    useState(false);
  const [notificationHintPopupVisible, setNotificationHintPopupVisible] =
    useState(false);
  const [
    notificationDisabledPopupVisible,
    setNotificationDisabledPopupVisible,
  ] = useState(false);

  const dispatch = useAppDispatch();

  const onChange = (checked: boolean) => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();

        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            setNotificationInBrowserEnabled(checked);
            dispatch(setBrowserNotificationEnabled(checked));

            const browserNotificationEnabledJSON = localStorage.getItem(
              'browserNotificationEnabled',
            );

            let nextBrowserNotificationEnabled: {
              browserNotificationEnabled: boolean;
              userId?: string;
            }[] = [];

            if (browserNotificationEnabledJSON) {
              const parsed = JSON.parse(browserNotificationEnabledJSON);

              if (parsed && Array.isArray(parsed)) {
                if (parsed?.find((item) => item.userId === userId)) {
                  nextBrowserNotificationEnabled = parsed?.map((item) => {
                    const result = { ...item };

                    if (item.userId === userId) {
                      result.browserNotificationEnabled = checked;
                    }

                    return result;
                  });
                } else {
                  nextBrowserNotificationEnabled.push({
                    browserNotificationEnabled: checked,
                    userId,
                  });
                }
              } else {
                nextBrowserNotificationEnabled = [
                  { browserNotificationEnabled: checked, userId },
                ];
              }
            } else {
              nextBrowserNotificationEnabled = [
                { browserNotificationEnabled: checked, userId },
              ];
            }

            localStorage.setItem(
              'browserNotificationEnabled',
              JSON.stringify(nextBrowserNotificationEnabled),
            );
          }
        });
      } else if (Notification.permission === 'granted') {
        setNotificationInBrowserEnabled(checked);
        dispatch(setBrowserNotificationEnabled(checked));

        const browserNotificationEnabledJSON = localStorage.getItem(
          'browserNotificationEnabled',
        );

        let nextBrowserNotificationEnabled: {
          browserNotificationEnabled: boolean;
          userId?: string;
        }[] = [];

        if (browserNotificationEnabledJSON) {
          const parsed = JSON.parse(browserNotificationEnabledJSON);

          if (parsed && Array.isArray(parsed)) {
            if (parsed?.find((item) => item.userId === userId)) {
              nextBrowserNotificationEnabled = parsed?.map((item) => {
                const result = { ...item };

                if (item.userId === userId) {
                  result.browserNotificationEnabled = checked;
                }

                return result;
              });
            } else {
              nextBrowserNotificationEnabled.push({
                browserNotificationEnabled: checked,
                userId,
              });
            }
          } else {
            nextBrowserNotificationEnabled = [
              { browserNotificationEnabled: checked, userId },
            ];
          }
        } else {
          nextBrowserNotificationEnabled = [
            { browserNotificationEnabled: checked, userId },
          ];
        }

        localStorage.setItem(
          'browserNotificationEnabled',
          JSON.stringify(nextBrowserNotificationEnabled),
        );
      } else if (Notification.permission === 'denied') {
        setNotificationDisabledPopupVisible(true);
      }
    }
  };

  useEffect(() => {
    setNotificationInBrowserEnabled(browserNotificationEnabled as boolean);
  }, [browserNotificationEnabled]);

  const macOS = [/Mac OS/i];

  const isMac = macOS.some((toMatchItem) =>
    navigator.userAgent.match(toMatchItem),
  );

  return (
    <>
      <div className={cn.employeeProfileTop}>
        <div className={cn.employeeProfileTopLeft}>
          <div
            className={cn.employeeProfileAvatar}
          >{`${employeeProfile?.profile?.firstName.charAt(
            0,
          )}${employeeProfile?.profile?.lastName.charAt(0)}`}</div>
          <div className={cn.employeeProfileInfo}>
            <div
              className={cn.employeeProfileInfoName}
            >{`${employeeProfile?.profile?.firstName} ${employeeProfile?.profile?.lastName}`}</div>
            {havePermissions && (
              <>
                <div className={cn.employeeProfileInfoRole}>
                  {roles?.find(
                    (item) =>
                      item.value === employeeProfile?.employeeRoleTemplateId,
                  )?.name || '-'}
                </div>
                <div
                  className={cn.employeeProfileInfoId}
                >{`ID: ${employeeProfile?.id}`}</div>
              </>
            )}
          </div>
        </div>
        <ButtonSecondary
          text={logoutInProgress ? 'Loading...' : 'Log out'}
          size={ButtonSize.SMALL}
          className={`button-white ${cn.logoutButton}`}
          onClick={logout}
          disabled={logoutInProgress}
          icon={<IconLogout />}
        />
      </div>
      <div className={cn.employeeProfileTitle}>Your profile</div>
      <div className={cn.employeeProfileContacts}>
        <div className={cn.employeeProfileContactBlock}>
          <div className={cn.employeeProfileContactBlockValue}>
            {employeeProfile?.email}
          </div>
          <div className={cn.employeeProfileContactBlockTitle}>
            Email corporate
          </div>
        </div>
        <div className={cn.employeeProfileContactBlock}>
          <div className={cn.employeeProfileContactBlockValue}>
            {employeeProfile?.emailPrivate || '-'}
          </div>
          <div className={cn.employeeProfileContactBlockTitle}>
            Email private
          </div>
        </div>
        <div className={cn.employeeProfileContactBlock}>
          <div className={cn.employeeProfileContactBlockValue}>
            {employeeProfile?.phone ? phoneFormat(employeeProfile?.phone) : '-'}
          </div>
          <div className={cn.employeeProfileContactBlockTitle}>Phone</div>
        </div>
      </div>
      <FiltersPresets userId={userId} />
      <div className={cn.employeeProfileTitle}>Notifications</div>
      <div className={cn.employeeProfilePermissionWrapper}>
        <div className={cn.employeeProfilePermissionPage}>
          <div className={cn.employeeProfilePermissionPageTitle}>
            Notifications in the browser
          </div>
          <div className={cn.employeeProfilePermissionPageActions}>
            <ToggleInput
              name="browserNotificationEnabled"
              title="Receive notifications in the browser"
              checked={notificationInBrowserEnabled}
              className={cn.notificationInBrowserEnabled}
              visualClassName={cn.notificationInBrowserEnabledVisual}
              onChange={(e) => onChange(e.target.checked)}
            />
            {isMac && (
              <button
                type="button"
                className={cn.browserNotificationHint}
                onClick={() =>
                  setNotificationHintPopupVisible(!notificationHintPopupVisible)
                }
              >
                <div className={cn.browserNotificationHintText}>
                  Make sure that notifications from your browser are enabled in
                  the settings
                </div>
                <div className={cn.browserNotificationHintButton}>
                  <IconInfo />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      {/* return when Security API will be complete */}
      <div className={cn.security} style={{ display: 'none' }}>
        <div className={cn.employeeProfileTitle}>Security</div>
        <div className={cn.securityContent}>
          <div className={cn.securityContentTitle}>Password</div>
          <div className={cn.securityContentValue}>
            <div className={cn.securityContentValueDescription}>
              To change your password, contact your administrator
            </div>
            <a
              target="_self"
              href="mailto:admin01@baseella.com"
              className={cn.securityContentValueLink}
            >
              admin01@baseella.com
            </a>
          </div>
        </div>
      </div>
      <NotifiactionHintPopup
        popupVisible={notificationHintPopupVisible}
        setPopupVisible={setNotificationHintPopupVisible}
      />
      <NotifiactionDisabledPopup
        popupVisible={notificationDisabledPopupVisible}
        setPopupVisible={setNotificationDisabledPopupVisible}
      />
    </>
  );
};

export default ProfileGeneral;
